// Example sentences for the sentence analyser. Every word is a FormRef resolved by the grammar engine; no form is
// typed by hand. The simple sentence and the sentence with a place are the कारक tool's own examples.

import type { FormRef } from '@/prakrit/lib/grammar/formref'
import type { Lang } from '@/prakrit/lib/grammar/types'

import {
    AV,
    K,
    N,
    SUBJ,
    V,
    formOf
} from '@/prakrit/lib/grammar/formref'
import { HINT_REFS, getKarakExample } from '@/prakrit/components/grammar/tools/karak-examples'

export interface VakyaExample {
    id: string
    label: Lang
    refs: FormRef[]
}

const karakRefs = (id: string, fallback: FormRef[]): FormRef[] => getKarakExample(id)?.refs ?? fallback

export const VAKYA_EXAMPLES: VakyaExample[] = [
    {
        id: 'simple',
        label: { hi: 'सरल वाक्य', en: 'A simple sentence' },
        refs: karakRefs('aham-paiyam-padhami', [SUBJ('uttam', 'eka'), N('पाइय', 'dvitiya', 'eka'), V('पढ', 'vartaman', 'uttam', 'eka')])
    },
    {
        id: 'two-subjects',
        label: { hi: 'दो कर्ता', en: 'Two subjects' },
        refs: [N('बालअ', 'prathama', 'eka'), AV('य'), N('बाला', 'prathama', 'eka'), AV('य'), V('खेल', 'vartaman', 'anya', 'bahu')]
    },
    {
        id: 'place',
        label: { hi: 'स्थान के साथ', en: 'With a place' },
        refs: karakRefs('balaa-uvavane-khelanti', [N('बालअ', 'prathama', 'bahu'), N('उववण', 'saptami', 'eka'), V('खेल', 'vartaman', 'anya', 'bahu')])
    },
    {
        id: 'participle',
        label: { hi: 'सम्बन्ध कृदन्त के साथ', en: 'With a participle' },
        refs: [SUBJ('anya', 'eka'), K('पढ', 'sambandh'), N('घर', 'dvitiya', 'eka'), V('गच्छ', 'vartaman', 'anya', 'eka')]
    },
    {
        id: 'giving',
        label: { hi: 'देना (चतुर्थी)', en: 'Giving' },
        refs: [N('माआ', 'prathama', 'eka'), N('पुत्त', 'chaturthi', 'eka'), N('फल', 'dvitiya', 'eka'), V('दा', 'vartaman', 'anya', 'eka')]
    },
    {
        id: 'mistake',
        label: { hi: 'गलती पकड़िए', en: 'Spot a mistake' },
        refs: [SUBJ('uttam', 'eka'), V('पढ', 'vartaman', 'anya', 'eka')]
    }
]

const cache = new Map<string, string>()

/** The example as one sentence with a दण्ड: अहं पाइयं पढामि। */
export const exampleSentence = (example: VakyaExample): string => {
    const hit = cache.get(example.id)

    if (hit) return hit

    const text = `${example.refs.map(formOf).join(' ')}।`

    cache.set(example.id, text)

    return text
}

/** The textarea's placeholder, the कारक tool's hint sentence (built by the engine). */
export const placeholderSentence = (): string => `${HINT_REFS.map(formOf).join(' ')}।`
